import type { FC, ReactNode } from 'react'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatCompactCurrency } from '@/lib/format'
import { LogoMark } from '@/components/generals/logo'
import { AnimatedNumber } from '@/components/generals/animated-number'
import { AreaChart } from '@/components/charts/area-chart'
import { BarChart } from '@/components/charts/bar-chart'
import { DonutChart } from '@/components/charts/donut-chart'
import { Sparkline } from '@/components/charts/sparkline'
import { DeltaBadge } from '@/components/charts/delta-badge'
import { ProgressBar } from '@/components/charts/progress-bar'
import { Panel } from '@/components/dashboard/panel'
import { InsightCard } from '@/components/dashboard/insight-card'
import { TransactionsTable } from '@/components/dashboard/transactions-table'

import {
    ChartLine,
    ChevronDown,
    FileText,
    LayoutDashboard,
    Plug,
    Receipt,
    Sparkles,
    Target
} from 'lucide-react'

import {
    CASH_SERIES,
    CHART_COLORS,
    COMPANY_NAME,
    EXPENSE_CATEGORIES,
    EXPENSE_SERIES,
    FORECAST_LABELS,
    INCOME_SERIES,
    INSIGHTS,
    MONTHS,
    NET_SERIES,
    PERFORMANCE,
    TRANSACTIONS,
    buildForecast
} from '@/constants/dashboard'

type RangeKey = 'month' | 'quarter' | 'half'

const RANGES: Array<{ key: RangeKey, label: string, size: number, caption: string }> = [
    { key: 'month', label: 'Month', size: 1, caption: 'September 2026 vs August' },
    { key: 'quarter', label: 'Quarter', size: 3, caption: 'Q3 2026 vs Q2' },
    { key: 'half', label: '6 months', size: 6, caption: 'Apr to Sep vs Oct to Mar' }
]

const NAV = [
    { label: 'Overview', Icon: LayoutDashboard },
    { label: 'Transactions', Icon: Receipt },
    { label: 'Reports', Icon: FileText },
    { label: 'Forecasts', Icon: ChartLine },
    { label: 'Budgets', Icon: Target },
    { label: 'Insights', Icon: Sparkles },
    { label: 'Integrations', Icon: Plug }
]

const sumWindow = (series: Array<number>, size: number, offset = 0): number => {
    const end = series.length - offset

    return series.slice(Math.max(0, end - size), end).reduce((total, value) => total + value, 0)
}

const change = (current: number, previous: number): number => {
    if (!previous) return 0

    return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10
}

interface DashboardAppProps {
    companyName?: string
    chrome?: boolean
    className?: string
}

export const DashboardApp: FC<DashboardAppProps> = ({
    companyName = COMPANY_NAME,
    chrome = true,
    className
}): ReactNode => {
    const [range, setRange] = useState<RangeKey>('month')
    const forecast = useMemo(() => buildForecast('base'), [])
    const active = RANGES.find(item => item.key === range) ?? RANGES[0]

    const kpis = useMemo(() => {
        const size = active.size
        const income = sumWindow(INCOME_SERIES, size)
        const expenses = sumWindow(EXPENSE_SERIES, size)
        const net = income - expenses
        const previousIncome = sumWindow(INCOME_SERIES, size, size)
        const previousExpenses = sumWindow(EXPENSE_SERIES, size, size)
        const cash = CASH_SERIES[CASH_SERIES.length - 1]
        const previousCash = CASH_SERIES[CASH_SERIES.length - 1 - size]

        return [
            { label: 'Total income', value: income, change: change(income, previousIncome), good: undefined, series: INCOME_SERIES, color: CHART_COLORS.gold },
            { label: 'Total expenses', value: expenses, change: change(expenses, previousExpenses), good: expenses <= previousExpenses, series: EXPENSE_SERIES, color: CHART_COLORS.burgundy },
            { label: 'Net cash flow', value: net, change: change(net, previousIncome - previousExpenses), good: undefined, series: NET_SERIES, color: CHART_COLORS.sage },
            { label: 'Cash balance', value: cash, change: change(cash, previousCash), good: undefined, series: CASH_SERIES, color: CHART_COLORS.terracotta }
        ]
    }, [active.size])

    return (
        <div
            className={cn(
                'overflow-hidden rounded-[22px] border border-burgundy/10 bg-paper text-left shadow-[0_50px_120px_-50px_rgb(57_26_24/0.55)]',
                className
            )}
        >
            <div className='flex'>
                {chrome && (
                    <aside aria-label='Workspace navigation preview' className='hidden w-[212px] shrink-0 flex-col border-r border-burgundy/[0.08] bg-ivory/70 p-4 xl:flex'>
                        <div className='flex items-center gap-2 px-1'>
                            <LogoMark className='size-6 text-gold-dark' />
                            <span className='font-heading text-lg font-medium tracking-[-0.03em] text-burgundy'>Veyra</span>
                        </div>

                        <div className='mt-5 flex items-center justify-between rounded-xl border border-burgundy/[0.08] bg-paper px-3 py-2'>
                            <span className='min-w-0'>
                                <span className='block font-mono text-[9px] uppercase tracking-[0.14em] text-stone'>Workspace</span>
                                <span className='block truncate text-xs font-medium text-burgundy'>{companyName}</span>
                            </span>

                            <ChevronDown aria-hidden='true' className='size-3.5 text-stone' />
                        </div>

                        <ul className='mt-5 space-y-0.5'>
                            {NAV.map(({ label, Icon }, index) => (
                                <li
                                    key={label}
                                    className={cn(
                                        'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-xs',
                                        index === 0 ? 'bg-burgundy text-cream' : 'text-stone-dark'
                                    )}
                                >
                                    <Icon aria-hidden='true' className='size-3.5' />
                                    {label}

                                    {label === 'Insights' && (
                                        <span className='ml-auto rounded-full bg-gold/30 px-1.5 text-[10px] font-medium text-gold-dark'>3</span>
                                    )}
                                </li>
                            ))}
                        </ul>

                        <div className='mt-auto rounded-xl border border-burgundy/[0.08] bg-paper p-3'>
                            <p className='flex items-center gap-2 text-[11px] font-medium text-burgundy'>
                                <span className='relative flex size-2'>
                                    <span className='absolute inline-flex size-full animate-ping rounded-full bg-sage opacity-60 motion-reduce:animate-none' />
                                    <span className='relative inline-flex size-2 rounded-full bg-sage-dark' />
                                </span>
                                All accounts synced
                            </p>

                            <p className='mt-1 text-[11px] text-stone'>12 connections, updated 2 min ago</p>
                        </div>
                    </aside>
                )}

                <div className='min-w-0 flex-1 p-3 sm:p-5 lg:p-6'>
                    <div className='mb-4 flex flex-wrap items-end justify-between gap-3 sm:mb-5'>
                        <div>
                            <p className='font-mono text-[10px] uppercase tracking-[0.16em] text-stone'>{companyName}</p>
                            <p className='mt-1 font-heading text-xl font-medium tracking-[-0.02em] text-burgundy sm:text-2xl'>Financial overview</p>
                            <p className='mt-0.5 text-[11px] text-stone'>{active.caption}</p>
                        </div>

                        <div role='group' aria-label='Reporting period' className='flex rounded-full border border-burgundy/10 bg-ivory p-0.5'>
                            {RANGES.map(item => (
                                <button
                                    key={item.key}
                                    type='button'
                                    aria-pressed={range === item.key}
                                    onClick={() => setRange(item.key)}
                                    className={cn(
                                        'rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors duration-300',
                                        range === item.key ? 'bg-burgundy text-cream' : 'text-stone-dark hover:text-burgundy'
                                    )}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
                        {kpis.map((kpi, index) => (
                            <div key={kpi.label} className='rounded-2xl border border-burgundy/[0.08] bg-ivory p-4'>
                                <p className='text-[11px] text-stone-dark'>{kpi.label}</p>

                                <div className='mt-2 flex items-end justify-between gap-3'>
                                    <div className='min-w-0'>
                                        <p className='font-heading text-[1.35rem] font-medium leading-none tracking-[-0.03em] text-burgundy'>
                                            <AnimatedNumber value={kpi.value} format='currency' />
                                        </p>

                                        <div className='mt-2'>
                                            <DeltaBadge value={kpi.change} good={kpi.good} />
                                        </div>
                                    </div>

                                    <Sparkline values={kpi.series} color={kpi.color} height={34} delay={index * 120} className='w-14 shrink-0' />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className='mt-3 grid gap-3 lg:grid-cols-12'>
                        <Panel
                            title='Income vs expenses'
                            subtitle='Last 12 months'
                            className='lg:col-span-7 2xl:col-span-8'
                            action={(
                                <div className='hidden items-center gap-3 text-[11px] text-stone-dark sm:flex'>
                                    <span className='flex items-center gap-1.5'><span aria-hidden='true' className='size-2 rounded-[2px] bg-gold' />Income</span>
                                    <span className='flex items-center gap-1.5'><span aria-hidden='true' className='size-2 rounded-[2px] bg-burgundy' />Expenses</span>
                                </div>
                            )}
                        >
                            <BarChart
                                title='Monthly income and expenses for the last 12 months'
                                labels={MONTHS}
                                series={[
                                    { name: 'Income', values: INCOME_SERIES, color: CHART_COLORS.gold },
                                    { name: 'Expenses', values: EXPENSE_SERIES, color: CHART_COLORS.burgundy }
                                ]}
                                height={190}
                                formatValue={formatCompactCurrency}
                                formatAxis={formatCompactCurrency}
                                labelEvery={2}
                                highlightIndex={11}
                            />
                        </Panel>

                        <Panel title='Spend by category' subtitle='September 2026' className='lg:col-span-5 2xl:col-span-4'>
                            <DonutChart
                                title='Expenses by category for September 2026'
                                segments={EXPENSE_CATEGORIES}
                                size={132}
                                centerLabel='Expenses'
                                formatValue={formatCompactCurrency}
                                legend='side'
                            />
                        </Panel>

                        <Panel title='Recent transactions' subtitle='Categorized and matched automatically' className='lg:col-span-7'>
                            <TransactionsTable transactions={TRANSACTIONS.slice(0, 5)} />
                        </Panel>

                        <Panel title='Cash flow forecast' subtitle='Base case, next 6 months' className='lg:col-span-5'>
                            <AreaChart
                                title='Cash balance, six months of actuals and a six month forecast'
                                labels={FORECAST_LABELS}
                                series={[{ name: 'Cash balance', values: forecast.values, color: CHART_COLORS.burgundy, fill: true, fadeFrom: forecast.splitIndex }]}
                                band={{ low: forecast.low, high: forecast.high, color: CHART_COLORS.gold, from: forecast.splitIndex }}
                                marker={{ index: forecast.splitIndex, label: 'Today' }}
                                height={150}
                                formatValue={formatCompactCurrency}
                                labelEvery={2}
                            />
                        </Panel>

                        <Panel title='Performance' subtitle='Key indicators this month' className='lg:col-span-5'>
                            <ul className='space-y-4'>
                                {PERFORMANCE.map((metric, index) => (
                                    <li key={metric.label}>
                                        <div className='mb-1.5 flex items-baseline justify-between gap-3 text-xs'>
                                            <span className='text-stone-dark'>{metric.label}</span>
                                            <span className='font-medium tabular-nums text-burgundy'>{metric.display}</span>
                                        </div>

                                        <ProgressBar value={metric.progress} color={index % 2 === 0 ? CHART_COLORS.burgundy : CHART_COLORS.gold} delay={index * 120} />
                                        <p className='mt-1 text-[10px] text-stone'>{metric.note}</p>
                                    </li>
                                ))}
                            </ul>
                        </Panel>

                        <Panel title='AI insights' subtitle='Generated from this week of activity' className='lg:col-span-7'>
                            <div className='grid gap-3 sm:grid-cols-2'>
                                {INSIGHTS.slice(0, 2).map(insight => (
                                    <InsightCard key={insight.id} insight={insight} />
                                ))}
                            </div>
                        </Panel>
                    </div>
                </div>
            </div>
        </div>
    )
}
